import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import ApiService from '../services/ApiService';
import { toast } from 'react-toastify';
import Loader from './Loader';
import { formatCurrency } from '../utils/helper';

const CheckoutForm = ({ amount, onSuccess, apiPrefix = '/customer', appointmentId, agreementText }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [error, setError] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [agreed, setAgreed] = useState(!agreementText);

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        setProcessing(true);

        try {
            // 1. Create Payment Intent on Backend
            const { data } = await ApiService.request({
                method: 'POST',
                url: `${apiPrefix}/create-payment-intent`,
                data: { amount, appointment_id: appointmentId }
            });

            if (!data.status) {
                throw new Error(data.message || 'Failed to initialize payment');
            }

            const clientSecret = data.data.clientSecret;

            // 2. Confirm Card Payment
            const result = await stripe.confirmCardPayment(clientSecret, {
                payment_method: {
                    card: elements.getElement(CardElement),
                }
            });

            if (result.error) {
                setError(result.error.message);
                toast.error(result.error.message);
            } else {
                if (result.paymentIntent.status === 'succeeded') {
                    toast.success('Payment successful!');
                    if (onSuccess) onSuccess(result.paymentIntent);
                }
            }
        } catch (err) {
            setError(err.message);
            toast.error(err.message);
        }

        setProcessing(false);
    };

    return (
        <form onSubmit={handleSubmit} className="p-4 border rounded">
            <h4 className="mb-4">Pay Online</h4>
            <div className="mb-4">
                <label className="form-label">Card Details</label>
                <div className="form-control p-3">
                    <CardElement 
                        options={{
                            hidePostalCode: true,
                            style: {
                                base: {
                                    fontSize: '16px',
                                    color: '#424770',
                                    '::placeholder': {
                                        color: '#aab7c4',
                                    },
                                },
                                invalid: {
                                    color: '#9e2146',
                                },
                            },
                        }} 
                    />
                </div>
            </div>
            {agreementText && (
                <div className="form-check mb-3">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        id="paymentAgreement"
                        checked={agreed}
                        onChange={(e) => setAgreed(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="paymentAgreement">
                        {agreementText}
                    </label>
                </div>
            )}
            {error && <div className="text-danger mb-3">{error}</div>}
            <button type="submit" className="btn btn-base w-100" disabled={!stripe || processing || !agreed}>
                {processing ? 'Processing...' : `Pay ${formatCurrency(amount)}`}
            </button>
        </form>
    );
};

const PaymentWrapper = (props) => {
    const [stripePromise, setStripePromise] = useState(null);
    const apiPrefix = props.apiPrefix || '/customer';

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const response = await ApiService.request({
                    method: 'GET',
                    url: `${apiPrefix}/payment-config`
                });
                if (response.data.status && response.data.data.publishableKey) {
                    setStripePromise(loadStripe(response.data.data.publishableKey));
                } else {
                    toast.error("Payment configuration missing");
                }
            } catch (error) {
                console.error("Failed to load stripe config", error);
                toast.error("Failed to load payment system");
            }
        };
        fetchConfig();
    }, []);

    if (!stripePromise) return <div className="text-center"><Loader /></div>;

    return (
        <Elements stripe={stripePromise}>
            <CheckoutForm {...props} apiPrefix={apiPrefix} />
        </Elements>
    );
};

export default PaymentWrapper;
